import React, { useEffect, useState, useCallback } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SiteDTO } from '@beable/roster-backend-client-v1';
import provisionService from '../../services/ProvisionService';
import { SiteTypes } from '@beable/content-types';
import { getStyles } from './styles';

interface SiteSelectorProps {
  siteId?: number;
  onSelect: any;
  className?: string;
  disabled?: boolean;
}

export default function EmployeeSiteSelector({ siteId, onSelect, className, disabled }: SiteSelectorProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [sites, setSites] = useState<SiteDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SiteDTO | undefined>(undefined);

  const memoOnSelect = useCallback(onSelect, []);

  useEffect(() => {
    const fetchSites = async () => {
      setSelectedSite(undefined);
      const data = await provisionService.getSitesByType([SiteTypes.presenter, SiteTypes.employee, SiteTypes.demo]);

      if (data) {
        data.sort((a, b) => (a.customerName! > b.customerName!) ? 1 : (a.customerName === b.customerName) ? ((a.subdomain > b.subdomain) ? 1 : -1) : -1)

        setSites(data);
        setIsLoading(false);

        let site = (data.length === 1) ? data[0] : undefined;
        if (!site && siteId) {
          site = data.find((s) => s.id === siteId);
        }
        setSelectedSite(site);
        memoOnSelect(site);
      } else {
        setIsLoading(false);
        window.alert("Could not fetch any sites")
      }
    }

    setIsLoading(true);
    fetchSites();
  }, [memoOnSelect, siteId]);

  const handleSiteChange = async (event: any) => {
    const selectedSite = sites.find((site: SiteDTO) => site.id === Number(event.target.value));
    setSelectedSite(selectedSite);
    if (onSelect) {
      onSelect(selectedSite);
    }
  };

  return (
    <Box className={className} sx={{
      width: "100%"
    }}>
      {isLoading && (
        <Box sx={styles.loaderContainer}>
          <CircularProgress disableShrink />
        </Box>
      )}
      {!isLoading && (
        <FormControl fullWidth variant='outlined'>
          <InputLabel>{!sites.length ? "No Sites Found" : "Select Site"}</InputLabel>
          <Select
            variant="outlined"
            data-testid="siteSelector"
            value={selectedSite?.id?.toString() ?? ''}
            onChange={handleSiteChange}
            disabled={sites.length < 2 || disabled}>
            {sites.map((site: any) => (
              <MenuItem key={site.id} value={site.id}>
                {`${site.customerName}: ${site.subdomain}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
}
